import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  getBniTravelCoverPrices,
  getTravelCoverPrices,
} from "../../../../api/quoteServices";
import closeicon from "../../../../assets/images/close.svg";
import erroricon from "../../../../assets/images/error.svg";
import BackdropModal from "../../../../components/common/modal/backdropModal";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import useTravelAddMoreCoversFlow from "../../../../hooks/useTravelAddMoreCoversFlow";
import {
  PolicyViewResponse,
  getCoverPricesLoading,
} from "../../../../redux/features/motor/getQuoteSlice";
import { selectNewMotorForm, setMotorFormData } from "../../../../redux/features/newMotorFormSlice";
import { currencyFormatter } from "../../../../utils";
import BottomStickyBtnLayout from "../../../motorInsurancePages/newMotorPolicy/Layouts/BottomStickyBtnLayout";
import PolicyCancellationMotorLayout from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/policyCancellationMotorLayout";
import { calculateAdditionalPremium } from "./data";
import { ADD_COVERS_ENHANCEMENT_TRAVEL } from "./reviewAndConfirmTravelPage";

const EnhanceYourCoverageTravelPage = ({
  handleNavigationToPolicyOverview,
}) => {
  const dispatch = useDispatch();
  const translate = useLanguageTranslate();

  // Get policy details from Redux
  const policyDetails = useSelector(PolicyViewResponse);
  const currentCovers = policyDetails?.PolicyHeader?.Covers || [];

  const { policy_id } = useParams();
  const { handleNextSubstep } = useTravelAddMoreCoversFlow();

  // Restore cover selection from Redux if available (fixes back navigation losing selection)
  const savedCoversFromRedux = useSelector(selectNewMotorForm)?.[ADD_COVERS_ENHANCEMENT_TRAVEL];

  // State for cover options and loading
  const [coverOptions, setCoverOptions] = useState([]);
  const [loadingCoverPrices, setLoadingCoverPrices] = useState(true);
  const [apiCoverPrices, setApiCoverPrices] = useState([]);

  // Get cover prices API loader
  const isCoverPriceLoading = useSelector(getCoverPricesLoading);

  const [formState, setFormState] = useState(
    savedCoversFromRedux?.coverOptionsState?.length > 0
      ? savedCoversFromRedux
      : { coverOptionsState: [] }
  );

  // Calculate total premium from selected covers whenever formState changes
  const [tempPrice, setTempPrice] = useState(0);

  // Modal state
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedCoverInfo, setSelectedCoverInfo] = useState(null);

  // Fetch cover prices from travel API
  const fetchCoverPrices = async () => {
    if (!policyDetails?.PolicyHeader) {
      console.log("No policy details available yet");
      return;
    }

    setLoadingCoverPrices(true);

    try {
      // Extract data from policy details
      const policyHeader = policyDetails.PolicyHeader;

      // Convert policy dates to required format
      const rawDepartureDate =
        policyHeader.FromDateStr || policyHeader.FromDate;
      const rawReturnDate = policyHeader.ToDateStr || policyHeader.ToDate;

      // Handle different date formats - convert to a standard format first
      const departureDate = formatDateForAPI(rawDepartureDate);
      const returnDate = formatDateForAPI(rawReturnDate);

      console.log("🗓️ Date conversion:");
      console.log(
        "- Raw departure:",
        rawDepartureDate,
        "→ Formatted:",
        departureDate
      );
      console.log("- Raw return:", rawReturnDate, "→ Formatted:", returnDate);

      // Validate dates before API call
      if (!departureDate || !returnDate) {
        console.error("❌ Invalid dates after formatting - cannot call API");
        console.error("Departure:", departureDate, "Return:", returnDate);
        setLoadingCoverPrices(false);
        return;
      }

      // New travel plan codes use the new cover-prices API, old codes use legacy API
      const newPlanCodes = ["SCHENGENB", "WLEXB", "WLB"];
      const isNewPlanCode = newPlanCodes.includes(policyHeader.PlanCode);

      // Map plan code to API format (API expects lowercase plan names)
      const planCodeMapping = {
        PL: "platinum",
        GO: "gold",
        SILVER: "silver",
      };

      const selectedPlan = isNewPlanCode
        ? policyHeader.PlanCode.toLowerCase() // getBniTravelCoverPrices accepts 'schengenb' / 'wlexb' / 'wlb'
        : planCodeMapping[policyHeader.PlanCode] || "silver";

      // Determine plan type from policy
      const planType = policyHeader.PlanType || "individual"; // fallback to individual

      console.log("🎯 Fetching travel cover prices for endorsement:");
      console.log("- Policy:", policy_id);
      console.log(
        "- Plan:",
        selectedPlan,
        "(from code:",
        policyHeader.PlanCode,
        ")"
      );
      console.log("- Plan Type:", planType);
      console.log("- Dates:", departureDate, "to", returnDate);
      console.log("- Policy Header Details:", {
        PlanCode: policyHeader.PlanCode,
        PlanName: policyHeader.PlanName,
        PlanType: policyHeader.PlanType,
        FromDateStr: policyHeader.FromDateStr,
        ToDateStr: policyHeader.ToDateStr,
        FromDate: policyHeader.FromDate,
        ToDate: policyHeader.ToDate,
      });

      const coverPricesFormData = {
        selectedPlan,
        departureDate,
        returnDate,
        planType,
      };

      const result = isNewPlanCode
        ? await getBniTravelCoverPrices(coverPricesFormData)
        : await getTravelCoverPrices(coverPricesFormData);

      console.log("📋 API Response:", result);
      console.log("📋 Success:", result.success);
      console.log("📋 Covers:", result.covers);
      console.log("📋 Error:", result.error);

      if (result.success && result.covers) {
        console.log("✅ Travel cover prices fetched:", result.covers);
        setApiCoverPrices(result.covers);

        // Transform API covers to component format
        const transformedCovers = result.covers.map((cover) => ({
          id: cover.CoverCode,
          name: getCoverDisplayName(cover.CoverCode, cover.CoverName),
          name_ar: getCoverDisplayName(cover.CoverCode, cover.CoverName, true),
          description: getDescriptionForCover(cover.CoverCode),
          description_ar: getDescriptionForCover(cover.CoverCode, true),
          price: parseFloat(cover.Price || 0),
          coverCode: cover.CoverCode,
          included: false,
          showCover: true,
          defaultIncludedInPlan: isAlreadyIncluded(cover.CoverCode),
          includedWithOther: false,
          disabled: false,
        }));

        setCoverOptions(transformedCovers);
        const initialFormState = { coverOptionsState: transformedCovers };
        setFormState(initialFormState);

        // Store initial state in Redux
        dispatch(
          setMotorFormData({
            form_name: ADD_COVERS_ENHANCEMENT_TRAVEL,
            data: initialFormState,
          })
        );

        console.log("📋 Transformed covers:", transformedCovers);
      } else {
        console.error("❌ Failed to fetch travel cover prices:", result.error);
        // Fallback to empty array
        setCoverOptions([]);
        setFormState({ coverOptionsState: [] });
      }
    } catch (error) {
      console.error("❌ Error fetching travel cover prices:", error);
      setCoverOptions([]);
      setFormState({ coverOptionsState: [] });
    } finally {
      setLoadingCoverPrices(false);
    }
  };

  // Helper function to get correct display names for cover codes
  const getCoverDisplayName = (coverCode, apiName = "", isArabic = false) => {
    const displayNames = {
      CPER137: {
        en: "Summer Activities",
        ar: "الأنشطة الصيفية",
      },
      CPER17: {
        en: "Winter Activities",
        ar: "الأنشطة الشتوية",
      },
    };

    // Return custom name if available, otherwise use API name
    return displayNames[coverCode]
      ? displayNames[coverCode][isArabic ? "ar" : "en"]
      : apiName;
  };

  // Helper function to get description for cover codes
  const getDescriptionForCover = (coverCode, isArabic = false) => {
    const descriptions = {
      CPER137: {
        en: "Coverage for summer sports and activities during your trip",
        ar: "تغطية الأنشطة والرياضات الصيفية أثناء رحلتك",
      },
      CPER17: {
        en: "Coverage for winter sports and activities during your trip",
        ar: "تغطية الأنشطة والرياضات الشتوية أثناء رحلتك",
      },
    };

    return descriptions[coverCode]
      ? descriptions[coverCode][isArabic ? "ar" : "en"]
      : "";
  };

  // Helper function to check if cover is already included in policy
  const isAlreadyIncluded = (coverCode) => {
    return currentCovers.some((cover) => cover.CoverCode === coverCode);
  };

  // Handle showing cover info modal
  const handleShowCoverInfo = (coverCode) => {
    setSelectedCoverInfo(coverCode);
    setShowInfoModal(true);
  };

  // Helper function to format dates for API
  const formatDateForAPI = (dateValue) => {
    if (!dateValue) return null;

    try {
      let date;

      // If it's already a Unix timestamp, convert to date first
      if (
        typeof dateValue === "number" ||
        (typeof dateValue === "string" && /^\d+$/.test(dateValue))
      ) {
        const timestamp = parseInt(dateValue);
        if (timestamp > 1000000000) {
          // Unix timestamp
          date = new Date(timestamp * 1000);
        } else {
          date = new Date(timestamp);
        }
      }
      // If it's a DD/MM/YYYY format string
      else if (typeof dateValue === "string" && dateValue.includes("/")) {
        const parts = dateValue.split("/");
        if (parts.length === 3) {
          // Assume DD/MM/YYYY format
          const day = parseInt(parts[0]);
          const month = parseInt(parts[1]) - 1; // Month is 0-indexed
          const year = parseInt(parts[2]);
          date = new Date(year, month, day);
        }
      }
      // Otherwise try to parse as standard date
      else {
        date = new Date(dateValue);
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.error("Invalid date:", dateValue);
        return null;
      }

      // Return in ISO format for the API
      return date.toISOString().split("T")[0]; // YYYY-MM-DD format
    } catch (error) {
      console.error("Date formatting error:", error, "for value:", dateValue);
      return null;
    }
  };

  // Fetch cover prices when policy data is available (skip if already restored from Redux)
  useEffect(() => {
    if (policyDetails?.PolicyHeader) {
      if (savedCoversFromRedux?.coverOptionsState?.length > 0) {
        // Restore from Redux - no need to fetch again
        console.log("✅ Restoring cover options from Redux (back navigation)");
        setCoverOptions(savedCoversFromRedux.coverOptionsState);
        setFormState(savedCoversFromRedux);
        setLoadingCoverPrices(false);
      } else {
        fetchCoverPrices();
      }
    }
  }, [policyDetails]);

  useEffect(() => {
    if (formState?.coverOptionsState?.length > 0) {
      const additionalPremium = calculateAdditionalPremium(
        formState.coverOptionsState
      );
      const tax = additionalPremium * 0.1; // 10% VAT
      const totalPremium = additionalPremium + tax;

      console.log("Travel EnhanceCoverage - Premium calculation:", {
        additionalPremium,
        tax,
        totalPremium,
        selectedCovers: formState.coverOptionsState
          .filter((opt) => opt.included)
          .map((opt) => ({
            code: opt.coverCode,
            name: opt.name,
            price: opt.price,
            included: opt.included,
            defaultIncludedInPlan: opt.defaultIncludedInPlan,
          })),
      });

      setTempPrice(totalPremium);
    } else {
      setTempPrice(0);
    }
  }, [formState]);

  // Updates selected cover
  const updateOptionValues = async (coverCode, updates, price) => {
    console.log(
      "🔄 Updating travel cover:",
      coverCode,
      updates,
      "Price:",
      price
    );

    // Create updated formState with the cover toggled
    const updatedCoverOptions = formState.coverOptionsState.map((option) => {
      if (option.coverCode === coverCode) {
        const updatedOption = {
          ...option,
          ...updates,
          // Ensure price is set correctly
          price: updates.price || option.price || price || 0,
        };
        console.log("📋 Updated cover option:", updatedOption);
        return updatedOption;
      }
      return option;
    });

    const update = {
      ...formState,
      coverOptionsState: updatedCoverOptions,
    };

    console.log("📊 Setting travel cover update:", update);
    console.log(
      "📊 Selected covers:",
      updatedCoverOptions.filter((opt) => opt.included)
    );

    // Update state
    setFormState(update);

    // Store in Redux for the review page
    dispatch(
      setMotorFormData({
        form_name: ADD_COVERS_ENHANCEMENT_TRAVEL,
        data: update,
      })
    );

    // Trigger immediate price recalculation
    const selectedCovers = updatedCoverOptions.filter(
      (opt) => opt.included && !opt.defaultIncludedInPlan
    );
    const additionalPremium = selectedCovers.reduce(
      (sum, cover) => sum + (parseFloat(cover.price) || 0),
      0
    );
    const tax = additionalPremium * 0.1;
    const totalPremium = additionalPremium + tax;

    console.log("💰 Immediate price calculation:", {
      selectedCovers: selectedCovers.length,
      additionalPremium,
      tax,
      totalPremium,
    });

    setTempPrice(totalPremium);
  };

  const { t } = useTranslation();

  // Next page handler
  const NextHandler = async () => {
    console.log("🚀 NextHandler clicked!");
    console.log("Travel Cover Options are: ", coverOptions);
    console.log("Travel Form State is: ", formState);
    console.log(
      "Selected covers:",
      formState.coverOptionsState?.filter((opt) => opt.included)
    );
    console.log("Total price:", tempPrice);

    try {
      console.log("🔄 Calling handleNextSubstep...");
      handleNextSubstep();
    } catch (error) {
      console.log("❌ Error in travel covers flow: ", error);
    }
  };

  return (
    <>
      <PolicyCancellationMotorLayout
        is_hide_back_btn={false}
        show_close_btn={false}
        isFirstStep={true}
        backClickHandler={handleNavigationToPolicyOverview}
        handleCloseBtnClick={handleNavigationToPolicyOverview}
        is_bottom_sticky_layout={false}
      >
        <BottomStickyBtnLayout
          show_summary={true}
          btn_onClick={NextHandler}
          price={tempPrice}
          btn_disabled={loadingCoverPrices}
          passedFrom="TravelAddCovers"
          btn_label={translate("Continue", "التالي")}
        >
          <div className="flex flex-col items-center p-6 max767:p-0 mx-auto pt-0 py-5 w-full pt-[1%]">
            <div className="w-[32%] max767:w-full mx-auto ipad:min-w-[66%] pb-5 max767:px-4">
              <div className="text-center max767:text-left rtl:max767:text-right mb-8">
                <h1 className="text-2xl font-semibold text-[#1A2024] mb-2">
                  {translate("Add More Covers", "إضافة المزيد من الأغطية")}
                </h1>
                <p className="text-[#1A2024]">
                  {translate(
                    "Would you like to add extra covers and benefits to your policy?",
                    "هل ترغب في إضافة تغطيات ومزايا إضافية إلى سياسة التأمين الخاصة بك؟"
                  )}
                </p>
              </div>
              {Array.isArray(formState.coverOptionsState) &&
              formState.coverOptionsState.length > 0 ? (
                <div className="space-y-4 mb-8 max-w-4xl mx-auto">
                  {formState.coverOptionsState
                    .slice()
                    .reverse()
                    .map((option) => (
                      <div
                        key={option.coverCode}
                        className={`bg-white rounded-lg border-2 p-4 transition-all duration-300 ${
                          option.included
                            ? "border-[#00719A]"
                            : "border-[#DDE2E4]"
                        } ${
                          option.defaultIncludedInPlan === true
                            ? "opacity-70 cursor-not-allowed"
                            : ""
                        }`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center">
                            <span className="text-base font-normal rtl:font-medium text-[#252C32] rtl:text-[#48535B] mr-2 rtl:mr-0 rtl:ml-2">
                              {translate(option.name, option.name_ar)}
                            </span>
                            {option.description && (
                                <button
                                  onClick={() =>
                                    handleShowCoverInfo(option.coverCode)
                                  }
                                  className="w-4 h-4 flex items-center justify-center hover:opacity-70 transition-opacity"
                                >
                                  <img
                                    src={erroricon}
                                    alt="info"
                                    className="w-4 h-4 mx-1"
                                  />
                                </button>
                              )}
                          </div>

                          {option.defaultIncludedInPlan ? (
                            <span className="bg-[#EEF0F2] text-[#252C32] px-3 py-1 rounded-md text-sm font-medium">
                              {translate(
                                "Included in the plan",
                                "شامل ضمن الخطة"
                              )}
                            </span>
                          ) : option.includedWithOther ? (
                            <span className="bg-[#EEF0F2] text-[#252C32] px-3 py-1 rounded-md text-sm font-medium">
                              {translate("Included", "متضمن")}
                            </span>
                          ) : (
                            <label
                              className={`relative inline-flex items-center ${
                                loadingCoverPrices
                                  ? "cursor-not-allowed opacity-50"
                                  : "cursor-pointer"
                              }`}
                            >
                              <input
                                type="checkbox"
                                className="sr-only"
                                checked={option.included}
                                disabled={option.disabled || loadingCoverPrices}
                                onChange={() =>
                                  !loadingCoverPrices &&
                                  updateOptionValues(
                                    option.coverCode,
                                    { included: !option.included },
                                    option.price
                                  )
                                }
                              />
                              <div
                                className={`w-8 h-5 rounded-full transition-colors ${
                                  option.included
                                    ? "bg-[#00A9E7]"
                                    : "bg-gray-300"
                                }`}
                              >
                                <div
                                  className={`size-4 bg-white rounded-full shadow transform transition-transform mt-0.5 ${
                                    option.included
                                      ? "translate-x-3.5 rtl:-translate-x-3.5"
                                      : "translate-x-[0.15rem] rtl:-translate-x-[0.15rem]"
                                  }`}
                                ></div>
                              </div>
                            </label>
                          )}
                        </div>

                        {/* Show price badge when inactive or active */}
                        {!option.defaultIncludedInPlan &&
                          !option.includedWithOther && (
                            <div className="flex justify-end">
                              {loadingCoverPrices || isCoverPriceLoading ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#00719A]"></div>
                              ) : (
                                <span
                                  className={`px-2 py-0.5 rounded-full text-xs font-medium uppercase ${
                                    option.included
                                      ? "bg-[#00719A] text-white"
                                      : "bg-[#EEF0F2] text-[#252C32]"
                                  }`}
                                >
                                  {translate(
                                    `+ BHD ${currencyFormatter(option.price)}`,
                                    `+ ${currencyFormatter(option.price)} د.ب`
                                  )}
                                </span>
                              )}
                            </div>
                          )}
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-lg text-[#48535B]">
                    {translate(
                      "No additional covers available for this plan.",
                      "لا توجد تغطيات إضافية متاحة لهذه الخطة."
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        </BottomStickyBtnLayout>
      </PolicyCancellationMotorLayout>

      {/* Info Modal */}
      <BackdropModal
        show={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        hideClosebtn
      >
        <div className="flex justify-between items-center mb-4 -mt-6">
          <h2 className="text-[#1A2024] max767:text-black text-2xl max-767:text-XL not-italic font-semibold leading-6">
            {selectedCoverInfo === "CPER17"
              ? translate(
                  "Winter Activities Coverage",
                  "تغطية الأنشطة الشتوية"
                )
              : selectedCoverInfo === "CPER137"
              ? translate(
                  "Summer Activities Coverage",
                  "تغطية الأنشطة الصيفية"
                )
              : translate("Coverage Information", "معلومات التغطية")}
          </h2>
          <button
            onClick={() => setShowInfoModal(false)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <img src={closeicon} alt="Close" className="w-6 h-6" />
          </button>
        </div>
        <div className="w-full max-h-[80vh] max-w-[540px]">
          {selectedCoverInfo === "CPER17" ? (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-[#000] mb-3">
                  {translate("Snow Sports:", "الرياضات الثلجية:")}
                </h3>
                <ul className="space-y-2 text-[#000] pl-2.5">
                  <li>
                    •{" "}
                    {translate(
                      "All skiing variations (traditional, land, mono, cross-country)",
                      "جميع أنواع التزلج (التقليدي والأرضي والأحادي والتزلج البري على الثلج)"
                    )}
                  </li>
                  <li>
                    •{" "}
                    {translate(
                      "Advanced skiing (heli-skiing, guided off-piste)",
                      "التزلج المتقدم (التزلج على الجليد و التزلج خارج الزحلقة)"
                    )}
                  </li>
                  <li>
                    •{" "}
                    {translate(
                      "Snowboarding & ski boarding",
                      "التزلج على الجليد والتزلج على الألواح"
                    )}
                  </li>
                  <li>
                    •{" "}
                    {translate(
                      "Snow recreation (ice skating, sledding, tobogganing)",
                      "الاستجمام على الجليد (التزلج على الجليد و التزحلق على الجليد)"
                    )}
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium text-[#000] mb-3">
                  {translate("Other Winter Activities:", "أنشطة شتوية أخرى:")}
                </h3>
                <ul className="space-y-2 text-[#000] pl-2.5">
                  <li>
                    •{" "}
                    {translate(
                      "Winter hiking & trekking",
                      "المشي لمسافات طويلة والتنزه الشتوي"
                    )}
                  </li>
                  <li>
                    •{" "}
                    {translate(
                      "Ice climbing",
                      "تسلق الجليد"
                    )}
                  </li>
                  <li>
                    •{" "}
                    {translate(
                      "Dog sledding & snowshoeing",
                      "التزلج بالكلاب والمشي بالمضارب الثلجية"
                    )}
                  </li>
                  <li>
                    • {translate("Ice fishing", "الصيد على الجليد")}
                  </li>
                </ul>
              </div>

              <div>
                <p className="text-sm text-[#000]">
                  <span className="font-bold">
                    {translate("Important:", "هام:")}
                  </span>{" "}
                  {translate(
                    "Professional competitions and tournaments are not covered.",
                    "لا يتم تغطية المسابقات والبطولات الاحترافية."
                  )}
                </p>
                <p className="text-xs text-[#5B6871] mt-2">
                  {translate(
                    "This protection ensures medical care and emergency assistance if accidents occur during these recreational activities.",
                    "تضمن هذه الحماية الرعاية الطبية والمساعدة الطارئة في حالة وقوع حوادث أثناء هذه الأنشطة الترفيهية."
                  )}
                </p>
              </div>
            </div>
          ) : selectedCoverInfo === "CPER137" ? (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-[#000] mb-3">
                  {translate("Water Sports:", "رياضات مائية:")}
                </h3>
                <ul className="space-y-2 text-[#000] pl-2.5">
                  <li>
                    •{" "}
                    {translate("Surfing", "ركوب الأمواج")}
                  </li>
                  <li>
                    •{" "}
                    {translate("Windsurfing", "ركوب الأمواج الشراعي")}
                  </li>
                  <li>
                    •{" "}
                    {translate("Sailing", "الإبحار")}
                  </li>
                  <li>
                    •{" "}
                    {translate("Water skiing", "التزلج المائي")}
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium text-[#000] mb-3">
                  {translate("Outdoor Summer Activities:", "أنشطة صيفية خارجية:")}
                </h3>
                <ul className="space-y-2 text-[#000] pl-2.5">
                  <li>
                    •{" "}
                    {translate("Beach sports", "رياضات الشاطئ")}
                  </li>
                  <li>
                    •{" "}
                    {translate("Snorkelling", "الغطس الحر")}
                  </li>
                  <li>
                    •{" "}
                    {translate("Recreational fishing", "صيد الأسماك الترفيهي")}
                  </li>
                </ul>
              </div>

              <div>
                <p className="text-sm text-[#000]">
                  <span className="font-bold">
                    {translate("Important:", "هام:")}
                  </span>{" "}
                  {translate(
                    "Professional competitions and tournaments are not covered.",
                    "لا يتم تغطية المسابقات والبطولات الاحترافية."
                  )}
                </p>
                <p className="text-xs text-[#5B6871] mt-2">
                  {translate(
                    "This protection ensures medical care and emergency assistance if accidents occur during these recreational activities.",
                    "تضمن هذه الحماية الرعاية الطبية والمساعدة الطارئة في حالة وقوع حوادث أثناء هذه الأنشطة الترفيهية."
                  )}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-[#000]">
                {translate(
                  "Coverage information not available.",
                  "معلومات التغطية غير متاحة."
                )}
              </p>
            </div>
          )}
        </div>
      </BackdropModal>
    </>
  );
};

export default EnhanceYourCoverageTravelPage;
